import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { charleyMemoryConfig } from './config';
import { fetchGuestbook, toggleGuestbookHidden, deleteGuestbookEntry, fetchTodos, createTodo, toggleTodoDone, deleteTodo, fetchEvents } from './api';

const ADMIN_TOKEN_KEY = 'cm_admin_token';

function getAdminToken() {
  try {
    return sessionStorage.getItem(ADMIN_TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

function setAdminToken(token) {
  try {
    if (token) sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  } catch {}
}

function clearAdminToken() {
  try {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {}
}

function getAuthHeaders() {
  const token = getAdminToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

const Page = styled.div`
  min-height: 100vh;
  padding: 80px 20px 40px;
  box-sizing: border-box;
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  color: #e9ecf1;
  background: radial-gradient(circle at top left, #343a4d 0, #070a12 60%);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  overflow-x: hidden;
`;

const Heading = styled.h1`
  font-size: 1.8rem;
  margin: 0 0 8px;
`;

const Subheading = styled.p`
  margin: 0 0 24px;
  opacity: 0.8;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Button = styled.button`
  border: none;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 0.95rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${(p) => (p.$primary ? '#f8f1da' : 'rgba(255, 255, 255, 0.08)')};
  color: ${(p) => (p.$primary ? '#24211c' : '#e9ecf1')};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  transition: transform 0.12s ease, box-shadow 0.12s ease, background-color 0.12s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.32);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
    box-shadow: none;
    transform: none;
  }
`;

const StatusText = styled.span`
  font-size: 0.9rem;
  opacity: 0.8;
`;

const Card = styled.div`
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 10px;
  background: rgba(12, 15, 25, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
`;

const CardTitle = styled.div`
  font-weight: 600;
`;

const CardMeta = styled.div`
  font-size: 0.8rem;
  opacity: 0.75;
  text-align: right;
  white-space: normal;
  word-break: break-word;
`;

const CardSummary = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
`;

const TagList = styled.div`
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.78rem;
`;

const Tag = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(250, 246, 228, 0.08);
  border: 1px solid rgba(250, 246, 228, 0.18);
`;

const ErrorBox = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  background: rgba(255, 99, 132, 0.08);
  border: 1px solid rgba(255, 99, 132, 0.5);
  color: #ffd8df;
  font-size: 0.9rem;
`;

const EmptyState = styled.div`
  margin-top: 16px;
  padding: 18px 16px;
  border-radius: 10px;
  border: 1px dashed rgba(255, 255, 255, 0.18);
  background: rgba(8, 11, 20, 0.8);
  font-size: 0.9rem;
  opacity: 0.85;
`;

const RecordsList = styled.div`
  margin-top: 10px;
`;

const GuestbookCard = styled.div`
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 8px;
  background: ${(p) => (p.$hidden ? 'rgba(12, 15, 25, 0.5)' : 'rgba(12, 15, 25, 0.9)')};
  border: 1px solid ${(p) => (p.$hidden ? 'rgba(255, 99, 132, 0.15)' : 'rgba(255, 255, 255, 0.06)')};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  opacity: ${(p) => (p.$hidden ? 0.6 : 1)};
`;

const GuestbookMeta = styled.div`
  font-size: 0.75rem;
  opacity: 0.55;
  margin-top: 6px;
  line-height: 1.5;
  word-break: break-all;
`;

const GuestbookMessage = styled.div`
  font-size: 0.92rem;
  white-space: pre-wrap;
  line-height: 1.5;
`;

const GuestbookAuthor = styled.span`
  font-weight: 600;
  font-size: 0.88rem;
  color: #faf6e4;
`;

const GuestbookActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const EventsFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  label { font-size: 0.8rem; opacity: 0.75; }
`;

const EventsTable = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(8, 11, 20, 0.6);
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-top: 4px;
`;

const StatsCard = styled.div`
  border-radius: 10px;
  padding: 10px 12px;
  background: rgba(12, 15, 25, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
`;

const StatsTitle = styled.div`
  font-size: 0.88rem;
  font-weight: 600;
  margin-bottom: 6px;
  opacity: 0.9;
`;

const StatsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.82rem;
`;

const StatsItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const EventsTableRow = styled.div`
  display: grid;
  grid-template-columns: 60px 140px 100px 1fr 80px minmax(120px, 1fr);
  gap: 8px;
  padding: 10px 12px;
  font-size: 0.85rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  align-items: start;
  &:last-child { border-bottom: none; }
  &.head { font-weight: 600; opacity: 0.9; }
`;

const PayloadCell = styled.div`
  word-break: break-all;
  max-height: 3em;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: monospace;
  font-size: 0.78rem;
`;

const SmallButton = styled.button`
  border: none;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.78rem;
  cursor: pointer;
  background: ${(p) => (p.$danger ? 'rgba(255, 99, 132, 0.15)' : 'rgba(255, 255, 255, 0.08)')};
  color: ${(p) => (p.$danger ? '#ffa0a0' : '#e9ecf1')};
  &:hover { opacity: 0.8; }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const LoginCard = styled.form`
  max-width: 360px;
  margin: 40px auto 0;
  padding: 28px 24px;
  border-radius: 12px;
  background: rgba(12, 15, 25, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const LoginLabel = styled.label`
  font-size: 0.82rem;
  opacity: 0.6;
  margin-bottom: -8px;
`;

const LoginInput = styled.input`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 10px 14px;
  color: #e9ecf1;
  font-size: 0.92rem;
  outline: none;
  &::placeholder { color: rgba(233, 236, 241, 0.35); }
  &:focus { border-color: rgba(250, 246, 228, 0.35); }
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 4px;
`;

const Tab = styled.button`
  border: none;
  background: none;
  color: ${(p) => (p.$active ? '#faf6e4' : 'rgba(233, 236, 241, 0.5)')};
  border-bottom: 2px solid ${(p) => (p.$active ? '#faf6e4' : 'transparent')};
  padding: 8px 16px;
  font-size: 0.95rem;
  cursor: pointer;
  transition: color 0.15s;
  &:hover { color: #e9ecf1; }

  @media (max-width: 640px) {
    padding: 6px 10px;
    font-size: 0.86rem;
    white-space: nowrap;
  }
`;

const AddTodoForm = styled.form`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const formatDate = (ts) => {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
};

const CharleyMemoryAdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [activeTab, setActiveTab] = useState('records');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [guestbookEntries, setGuestbookEntries] = useState([]);
  const [guestbookLoading, setGuestbookLoading] = useState(false);
  const [guestbookError, setGuestbookError] = useState('');

  const [todos, setTodos] = useState([]);
  const [todosLoading, setTodosLoading] = useState(false);
  const [todosError, setTodosError] = useState('');
  const [newTodoText, setNewTodoText] = useState('');
  const [newTodoCategory, setNewTodoCategory] = useState('');
  const [newTodoType, setNewTodoType] = useState('dev'); // 'shared' | 'dev' — 預設「開發」
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState(''); // '' | 'shared' | 'dev'
  const [addingTodo, setAddingTodo] = useState(false);

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const getCurrentWeekRange = () => {
    const now = new Date();
    const day = now.getDay();
    const diffToSunday = now.getDate() - day;
    const start = new Date(now);
    start.setDate(diffToSunday);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return {
      from: start.toISOString().slice(0, 10),
      to: end.toISOString().slice(0, 10),
    };
  };
  const defaultWeek = useMemo(getCurrentWeekRange, []);
  const [filterFrom, setFilterFrom] = useState(defaultWeek.from);
  const [filterTo, setFilterTo] = useState(defaultWeek.to);
  const [filterEventType, setFilterEventType] = useState('');
  const [filterSessionId, setFilterSessionId] = useState('');

  const {
    pageViewEvents,
    pageStatsArray,
    dailyStatsArray,
    totalPageViews,
    uniqueSessions,
    tagStatsArray,
    totalTagClicks,
  } = useMemo(() => {
    const pageViewEvents = Array.isArray(events)
      ? events.filter((ev) => ev.event_type === 'page_view')
      : [];
    const tagClickEvents = Array.isArray(events)
      ? events.filter((ev) => ev.event_type === 'tag_click')
      : [];

    const pageStatsMap = new Map();
    const dailyMap = new Map();
    const sessionSet = new Set();
    const tagStatsMap = new Map();

    const normalizePayload = (payload) => {
      if (!payload) return {};
      if (typeof payload === 'string') {
        try {
          return JSON.parse(payload);
        } catch {
          return {};
        }
      }
      if (typeof payload === 'object') return payload;
      return {};
    };

    pageViewEvents.forEach((ev) => {
      const payload = normalizePayload(ev.payload);
      const path =
        typeof payload.path === 'string' && payload.path
          ? payload.path
          : '[unknown]';

      const createdAt = ev.created_at || ev.createdAt;
      let dateKey = '';
      if (createdAt) {
        const d = new Date(createdAt);
        if (!Number.isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          dateKey = `${y}-${m}-${day}`;
        }
      }

      const prev = pageStatsMap.get(path) || { path, count: 0 };
      prev.count += 1;
      pageStatsMap.set(path, prev);

      if (dateKey) {
        const dayRow = dailyMap.get(dateKey) || { date: dateKey, total: 0 };
        dayRow.total += 1;
        dailyMap.set(dateKey, dayRow);
      }

      if (ev.session_id) {
        sessionSet.add(ev.session_id);
      }
    });

    tagClickEvents.forEach((ev) => {
      const payload = normalizePayload(ev.payload);
      const tag =
        typeof payload.tag === 'string' && payload.tag
          ? payload.tag
          : '[unknown]';
      const prev = tagStatsMap.get(tag) || { tag, count: 0 };
      prev.count += 1;
      tagStatsMap.set(tag, prev);
    });

    const pageStatsArray = Array.from(pageStatsMap.values()).sort(
      (a, b) => b.count - a.count
    );
    const dailyStatsArray = Array.from(dailyMap.values()).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
    const tagStatsArray = Array.from(tagStatsMap.values()).sort(
      (a, b) => b.count - a.count
    );

    return {
      pageViewEvents,
      pageStatsArray,
      dailyStatsArray,
      totalPageViews: pageViewEvents.length,
      uniqueSessions: sessionSet.size,
      tagStatsArray,
      totalTagClicks: tagClickEvents.length,
    };
  }, [events]);

  const initialLoadDoneRef = useRef(false);

  const apiBase = useMemo(() => {
    const base = charleyMemoryConfig.apiBaseUrl || '';
    return base.replace(/\/$/, '');
  }, []);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    if (!apiBase) {
      setLoginError('後端 API base URL 尚未設定。');
      return;
    }
    const username = loginUsername.trim();
    const password = loginPassword;
    if (!username || !password) {
      setLoginError('請輸入帳號和密碼。');
      return;
    }
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await fetch(`${apiBase}/charley-memory/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || '登入失敗');
      }
      if (typeof body.token === 'string' && body.token) {
        setAdminToken(body.token);
      }
      setIsLoggedIn(true);
      setLoginPassword('');
    } catch (err) {
      setLoginError(err.message || '登入失敗，請確認帳號密碼。');
    } finally {
      setLoginLoading(false);
    }
  }, [apiBase, loginUsername, loginPassword]);

  const handleLogout = useCallback(async () => {
    try {
      if (apiBase) {
        await fetch(`${apiBase}/auth/logout`, {
          method: 'POST',
          headers: { ...getAuthHeaders() },
          credentials: 'include',
        }).catch(() => {});
      }
    } finally {
      clearAdminToken();
      setIsLoggedIn(false);
      setRecords([]);
      setGuestbookEntries([]);
      setTodos([]);
      setEvents([]);
    }
  }, [apiBase]);

  const fetchRecords = useCallback(async () => {
    if (!apiBase) {
      setError('後端 API base URL 尚未設定。');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/charley-memory/records`, {
        headers: { ...getAuthHeaders() },
        credentials: 'include',
      });
      if (res.status === 401 || res.status === 403) {
        setError('Session 已過期，請重新登入。');
        setIsLoggedIn(false);
        clearAdminToken();
        setRecords([]);
        return;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(`讀取 records 失敗：${err.message || String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  const loadGuestbook = useCallback(async () => {
    setGuestbookLoading(true);
    setGuestbookError('');
    try {
      const entries = await fetchGuestbook();
      setGuestbookEntries(Array.isArray(entries) ? entries : []);
    } catch (err) {
      setGuestbookError(`讀取留言失敗：${err.message || String(err)}`);
    } finally {
      setGuestbookLoading(false);
    }
  }, []);

  const handleToggleHidden = useCallback(async (id) => {
    try {
      await toggleGuestbookHidden(id);
      setGuestbookEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, is_hidden: !e.is_hidden } : e))
      );
    } catch (err) {
      alert(`操作失敗：${err.message}`);
    }
  }, []);

  const handleDeleteEntry = useCallback(async (id) => {
    if (!window.confirm('確定要刪除這則留言嗎？')) return;
    try {
      await deleteGuestbookEntry(id);
      setGuestbookEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      alert(`刪除失敗：${err.message}`);
    }
  }, []);

  const loadTodos = useCallback(async () => {
    setTodosLoading(true);
    setTodosError('');
    try {
      const data = await fetchTodos();
      setTodos(Array.isArray(data) ? data : []);
    } catch (err) {
      setTodosError(`讀取待辦事項失敗：${err.message || String(err)}`);
    } finally {
      setTodosLoading(false);
    }
  }, []);

  const allTodoCategories = useMemo(() => {
    const s = new Set();
    for (const t of todos) {
      const c = (t?.category || '').trim();
      if (c) s.add(c);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'zh-Hant'));
  }, [todos]);

  const visibleTodos = useMemo(() => {
    let list = todos;
    if (typeFilter) {
      list = list.filter((t) => (t?.type || '').trim() === typeFilter);
    }
    if (!categoryFilter) return list;
    return list.filter((t) => (t?.category || '').trim() === categoryFilter);
  }, [categoryFilter, typeFilter, todos]);

  const handleAddTodo = useCallback(async (e) => {
    e.preventDefault();
    const text = newTodoText.trim();
    if (!text) return;
    const category = newTodoCategory.trim();
    const type = (newTodoType || '').trim() || 'dev';
    setAddingTodo(true);
    try {
      const created = await createTodo({ text, createdBy: '', category, type });
      setTodos((prev) => [created, ...prev]);
      setNewTodoText('');
      setNewTodoCategory('');
      setNewTodoType('dev');
    } catch (err) {
      alert(`新增失敗：${err.message}`);
    } finally {
      setAddingTodo(false);
    }
  }, [newTodoCategory, newTodoText, newTodoType]);

  const handleToggleTodo = useCallback(async (id) => {
    setTodos((prev) => prev.map((t) => (t.id === id ? { ...t, is_done: !t.is_done } : t)));
    try { await toggleTodoDone(id); } catch { loadTodos(); }
  }, [loadTodos]);

  const handleDeleteTodo = useCallback(async (id) => {
    if (!window.confirm('確定要刪除這個待辦事項嗎？')) return;
    try {
      await deleteTodo(id);
      setTodos((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      alert(`刪除失敗：${err.message}`);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    setEventsError('');
    try {
      const opts = {};
      if (filterFrom) {
        const d = new Date(filterFrom);
        d.setHours(0, 0, 0, 0);
        opts.from = d.toISOString();
      }
      if (filterTo) {
        const d = new Date(filterTo);
        d.setHours(23, 59, 59, 999);
        opts.to = d.toISOString();
      }
      if (filterEventType.trim()) opts.event_type = filterEventType.trim();
      if (filterSessionId.trim()) opts.session_id = filterSessionId.trim();
      const data = await fetchEvents(opts);
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setEventsError(`讀取活動紀錄失敗：${err.message || String(err)}`);
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [filterFrom, filterTo, filterEventType, filterSessionId]);

  // On mount, try fetching records to check if session cookie is still valid
  useEffect(() => {
    if (!apiBase) return;
    fetch(`${apiBase}/charley-memory/records`, { headers: { ...getAuthHeaders() }, credentials: 'include' })
      .then((res) => {
        if (res.ok) {
          initialLoadDoneRef.current = true;
          setIsLoggedIn(true);
          return res.json();
        }
        if (res.status === 401 || res.status === 403) {
          clearAdminToken();
        }
        return null;
      })
      .then((data) => {
        if (data && Array.isArray(data)) setRecords(data);
      })
      .catch(() => {});
  }, [apiBase]);

  // After explicit login (not from mount session check), fetch records
  useEffect(() => {
    if (!isLoggedIn || !apiBase) return;
    if (initialLoadDoneRef.current) {
      initialLoadDoneRef.current = false;
      return;
    }
    fetchRecords();
  }, [isLoggedIn, fetchRecords, apiBase]);

  if (!isLoggedIn) {
    return (
      <Page>
        <Heading>Charley Memory 後台</Heading>
        <Subheading>請先登入以管理後台。</Subheading>
        <LoginCard onSubmit={handleLogin}>
          <LoginLabel>帳號</LoginLabel>
          <LoginInput
            type="text"
            placeholder="username"
            value={loginUsername}
            onChange={(e) => setLoginUsername(e.target.value)}
            autoComplete="username"
            autoFocus
          />
          <LoginLabel>密碼</LoginLabel>
          <LoginInput
            type="password"
            placeholder="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            autoComplete="current-password"
          />
          {loginError && <ErrorBox>{loginError}</ErrorBox>}
          <Button type="submit" $primary disabled={loginLoading}>
            {loginLoading ? '登入中…' : '登入'}
          </Button>
        </LoginCard>
      </Page>
    );
  }

  return (
    <Page>
      <Heading>Charley Memory 後台</Heading>
      <Subheading>管理 date records、照片與留言。</Subheading>

      <Toolbar>
        <Button type="button" onClick={fetchRecords} disabled={loading}>
          {loading ? '讀取中…' : '重新載入 records'}
        </Button>
        <Button type="button" onClick={handleLogout}>
          登出
        </Button>
        <StatusText>
          {apiBase ? `API: ${apiBase}` : '尚未設定 API base URL'}
        </StatusText>
      </Toolbar>

      <TabBar>
        <Tab $active={activeTab === 'records'} onClick={() => setActiveTab('records')}>
          Records ({records.length})
        </Tab>
        <Tab $active={activeTab === 'guestbook'} onClick={() => {
          setActiveTab('guestbook');
          if (guestbookEntries.length === 0 && !guestbookLoading) loadGuestbook();
        }}>
          留言板 ({guestbookEntries.length})
        </Tab>
        <Tab $active={activeTab === 'todos'} onClick={() => {
          setActiveTab('todos');
          if (todos.length === 0 && !todosLoading) loadTodos();
        }}>
          待辦 ({todos.length})
        </Tab>
        <Tab $active={activeTab === 'events'} onClick={() => setActiveTab('events')}>
          活動紀錄
        </Tab>
      </TabBar>

      {activeTab === 'records' && (
        <>
          {error && <ErrorBox>{error}</ErrorBox>}
          {records.length === 0 && !loading ? (
            <EmptyState>
              目前沒有任何 date record。
            </EmptyState>
          ) : (
            <RecordsList>
              {records.map((r) => (
                <Card key={r.id}>
                  <CardHeader>
                    <CardTitle>{r.title || r.summary || r.id}</CardTitle>
                    <CardMeta>
                      <div>{r.id}</div>
                      <div>{formatDate(r.timestamp)}</div>
                    </CardMeta>
                  </CardHeader>
                  {r.summary && <CardSummary>{r.summary}</CardSummary>}
                  {Array.isArray(r.tags) && r.tags.length > 0 && (
                    <TagList>
                      {r.tags.map((tag) => (
                        <Tag key={tag}>#{tag}</Tag>
                      ))}
                    </TagList>
                  )}
                </Card>
              ))}
            </RecordsList>
          )}
        </>
      )}

      {activeTab === 'guestbook' && (
        <>
          <Toolbar>
            <Button type="button" onClick={loadGuestbook} disabled={guestbookLoading}>
              {guestbookLoading ? '讀取中…' : '重新載入留言'}
            </Button>
            <StatusText>共 {guestbookEntries.length} 則留言</StatusText>
          </Toolbar>
          {guestbookError && <ErrorBox>{guestbookError}</ErrorBox>}
          {guestbookEntries.length === 0 && !guestbookLoading ? (
            <EmptyState>目前沒有任何留言。</EmptyState>
          ) : (
            <RecordsList>
              {guestbookEntries.map((entry) => (
                <GuestbookCard key={entry.id} $hidden={entry.is_hidden}>
                  <CardHeader>
                    <GuestbookAuthor>{entry.author_name || '匿名'}</GuestbookAuthor>
                    <CardMeta>{formatDate(entry.created_at)}</CardMeta>
                  </CardHeader>
                  <GuestbookMessage>{entry.message}</GuestbookMessage>
                  <GuestbookMeta>
                    ID: {entry.id} · IP: {entry.ip || '-'} · UA: {entry.user_agent || '-'}
                    {entry.is_hidden && ' · [已隱藏]'}
                  </GuestbookMeta>
                  <GuestbookActions>
                    <SmallButton type="button" onClick={() => handleToggleHidden(entry.id)}>
                      {entry.is_hidden ? '取消隱藏' : '隱藏'}
                    </SmallButton>
                    <SmallButton
                      type="button"
                      $danger
                      onClick={() => handleDeleteEntry(entry.id)}
                      aria-label="刪除"
                      title="刪除"
                      style={{ width: 28, padding: '2px 0', textAlign: 'center' }}
                    >
                      ×
                    </SmallButton>
                  </GuestbookActions>
                </GuestbookCard>
              ))}
            </RecordsList>
          )}
        </>
      )}

      {activeTab === 'todos' && (
        <>
          <Toolbar>
            <Button type="button" onClick={loadTodos} disabled={todosLoading}>
              {todosLoading ? '讀取中…' : '重新載入待辦'}
            </Button>
            <LoginInput
              as="select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              aria-label="類型篩選"
              style={{ width: 180, paddingTop: 8, paddingBottom: 8 }}
            >
              <option value="">全部類型</option>
              <option value="shared">生活</option>
              <option value="dev">開發</option>
            </LoginInput>
            <LoginInput
              as="select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              aria-label="分類篩選"
              style={{ width: 220, paddingTop: 8, paddingBottom: 8 }}
            >
              <option value="">全部分類</option>
              {allTodoCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </LoginInput>
            <StatusText>
              {typeFilter || categoryFilter
                ? `共 ${visibleTodos.length}/${todos.length} 項${typeFilter ? `（類型：${typeFilter === 'dev' ? '開發' : '生活'}）` : ''}${categoryFilter ? `（分類：${categoryFilter}）` : ''}`
                : `共 ${todos.length} 項`}
            </StatusText>
          </Toolbar>
          {todosError && <ErrorBox>{todosError}</ErrorBox>}
          <AddTodoForm onSubmit={handleAddTodo}>
            <LoginInput
              type="text"
              placeholder="新增待辦事項…"
              value={newTodoText}
              onChange={(e) => setNewTodoText(e.target.value)}
              maxLength={1000}
              style={{ flex: 1 }}
            />
            <LoginInput
              as="select"
              value={newTodoType}
              onChange={(e) => setNewTodoType(e.target.value)}
              aria-label="待辦類型"
              style={{ width: 140, paddingTop: 8, paddingBottom: 8 }}
            >
              <option value="shared">生活</option>
              <option value="dev">開發</option>
            </LoginInput>
            <LoginInput
              type="text"
              placeholder="分類（可選）"
              list="cm-admin-todo-categories"
              value={newTodoCategory}
              onChange={(e) => setNewTodoCategory(e.target.value)}
              maxLength={80}
              style={{ width: 180 }}
            />
            <Button type="submit" $primary disabled={addingTodo || !newTodoText.trim()}>
              {addingTodo ? '…' : '新增'}
            </Button>
          </AddTodoForm>
          <datalist id="cm-admin-todo-categories">
            {allTodoCategories.map((c) => <option key={c} value={c} />)}
          </datalist>
          {visibleTodos.length === 0 && !todosLoading ? (
            <EmptyState>
              {todos.length === 0 ? '目前沒有待辦事項。' : (categoryFilter ? `分類「${categoryFilter}」目前沒有待辦事項。` : '目前沒有待辦事項。')}
            </EmptyState>
          ) : (
            <RecordsList>
              {visibleTodos.map((t) => (
                <Card key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, opacity: t.is_done ? 0.5 : 1 }}>
                  <SmallButton
                    type="button"
                    onClick={() => handleToggleTodo(t.id)}
                    aria-label={t.is_done ? '標記為未完成' : '標記為已完成'}
                    style={{ flexShrink: 0, fontSize: '0.85rem', marginTop: 1 }}
                  >
                    {t.is_done ? '✓' : '○'}
                  </SmallButton>
                  <div style={{ flex: 1 }}>
                    <CardTitle style={{ textDecoration: t.is_done ? 'line-through' : 'none' }}>{t.text}</CardTitle>
                    <CardMeta style={{ textAlign: 'left', marginTop: 4 }}>
                      {t.type === 'dev' ? '開發' : '生活'}
                      {t.category ? ` · ${t.category}` : ''}
                      {' · '}{formatDate(t.created_at)}
                    </CardMeta>
                  </div>
                  <SmallButton
                    type="button"
                    $danger
                    onClick={() => handleDeleteTodo(t.id)}
                    aria-label="刪除待辦"
                    title="刪除"
                    style={{ width: 28, padding: '2px 0', textAlign: 'center' }}
                  >
                    ×
                  </SmallButton>
                </Card>
              ))}
            </RecordsList>
          )}
        </>
      )}

      {activeTab === 'events' && (
        <>
          <EventsFilters>
            <FilterField>
              <label>開始日期</label>
              <LoginInput
                type="date"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
              />
            </FilterField>
            <FilterField>
              <label>結束日期</label>
              <LoginInput
                type="date"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
              />
            </FilterField>
            <FilterField>
              <label>事件類型</label>
              <LoginInput
                type="text"
                placeholder="例如 page_view"
                value={filterEventType}
                onChange={(e) => setFilterEventType(e.target.value)}
              />
            </FilterField>
            <FilterField>
              <label>使用者 (session_id 或 IP 前綴)</label>
              <LoginInput
                type="text"
                placeholder="session 前幾碼或 IP 前綴"
                value={filterSessionId}
                onChange={(e) => setFilterSessionId(e.target.value)}
              />
            </FilterField>
            <Button type="button" onClick={loadEvents} disabled={eventsLoading} $primary>
              {eventsLoading ? '查詢中…' : '查詢'}
            </Button>
            <StatusText>共 {events.length} 筆（最多 500 筆）</StatusText>
          </EventsFilters>
          {pageViewEvents.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <StatusText>
                這段時間的 page_view 共 {totalPageViews} 次，約 {uniqueSessions || 0} 個 session，tag_click 共 {totalTagClicks} 次。
              </StatusText>
              <DashboardGrid>
                <StatsCard>
                  <StatsTitle>熱門頁面</StatsTitle>
                  {pageStatsArray.length === 0 ? (
                    <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                      沒有 page_view 資料。
                    </div>
                  ) : (
                    <StatsList>
                      {pageStatsArray.slice(0, 5).map((row) => (
                        <StatsItem key={row.path}>
                          <span style={{ wordBreak: 'break-all' }}>{row.path}</span>
                          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.count} 次</span>
                        </StatsItem>
                      ))}
                    </StatsList>
                  )}
                </StatsCard>
                <StatsCard>
                  <StatsTitle>最常點的標籤</StatsTitle>
                  {tagStatsArray.length === 0 ? (
                    <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                      沒有 tag_click 資料。
                    </div>
                  ) : (
                    <StatsList>
                      {tagStatsArray.slice(0, 8).map((row) => (
                        <StatsItem key={row.tag}>
                          <span style={{ wordBreak: 'break-all' }}>#{row.tag}</span>
                          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.count} 次</span>
                        </StatsItem>
                      ))}
                    </StatsList>
                  )}
                </StatsCard>
                <StatsCard>
                  <StatsTitle>每天瀏覽次數</StatsTitle>
                  {dailyStatsArray.length === 0 ? (
                    <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                      沒有資料。
                    </div>
                  ) : (
                    <StatsList>
                      {dailyStatsArray.slice(0, 10).map((row) => (
                        <StatsItem key={row.date}>
                          <span>{row.date}</span>
                          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.total} 次</span>
                        </StatsItem>
                      ))}
                    </StatsList>
                  )}
                </StatsCard>
              </DashboardGrid>
            </div>
          )}
          {eventsError && <ErrorBox>{eventsError}</ErrorBox>}
          {events.length === 0 && !eventsLoading ? (
            <EmptyState>設定篩選後按「查詢」載入活動紀錄；不設條件則顯示全部。</EmptyState>
          ) : (
            <EventsTable>
              <EventsTableRow className="head">
                <div>ID</div>
                <div>時間</div>
                <div>類型</div>
                <div>payload</div>
                <div>IP</div>
                <div>session_id</div>
              </EventsTableRow>
              {events.map((ev) => (
                <EventsTableRow key={ev.id}>
                  <div>{ev.id}</div>
                  <div>{formatDate(ev.created_at)}</div>
                  <div>{ev.event_type}</div>
                  <PayloadCell title={typeof ev.payload === 'string' ? ev.payload : JSON.stringify(ev.payload)}>
                    {typeof ev.payload === 'string' ? ev.payload : JSON.stringify(ev.payload)}
                  </PayloadCell>
                  <div>{ev.ip || '-'}</div>
                  <div style={{ wordBreak: 'break-all', fontSize: '0.78rem' }}>{ev.session_id || '-'}</div>
                </EventsTableRow>
              ))}
            </EventsTable>
          )}
        </>
      )}
    </Page>
  );
};

export default CharleyMemoryAdminPage;

